import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FiEdit, FiTrash2, FiArrowLeft, FiCheckCircle, FiXCircle, 
  FiAlertCircle, FiRefreshCw, FiToggleLeft, FiToggleRight 
} from 'react-icons/fi';
import integrationService from '../../services/integrationService';

const IntegrationView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntegration();
  }, [id]);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const data = await integrationService.getIntegrationById(id);
      setIntegration(data.integration);
    } catch (error) {
      toast.error('Failed to fetch integration');
      console.error('Error fetching integration:', error);
      navigate('/app/integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      try {
        await integrationService.deleteIntegration(id);
        toast.success('Integration deleted successfully');
        navigate('/app/integrations');
      } catch (error) {
        toast.error('Failed to delete integration');
        console.error('Error deleting integration:', error);
      }
    }
  };

  const handleToggle = async () => {
    try {
      await integrationService.toggleIntegration(id);
      toast.success('Integration status updated');
      fetchIntegration();
    } catch (error) {
      toast.error('Failed to toggle integration');
      console.error('Error toggling integration:', error);
    }
  };

  const handleTest = async () => {
    try {
      const result = await integrationService.testIntegration(id);
      if (result.result.success) {
        toast.success('Integration test successful');
      } else {
        toast.error('Integration test failed');
      }
      fetchIntegration();
    } catch (error) {
      toast.error('Failed to test integration');
      console.error('Error testing integration:', error);
    }
  };

  const handleSync = async () => {
    try {
      await integrationService.syncIntegration(id);
      toast.success('Integration sync initiated');
      fetchIntegration();
    } catch (error) {
      toast.error('Failed to sync integration');
      console.error('Error syncing integration:', error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', icon: FiCheckCircle },
      inactive: { color: 'bg-gray-100 text-gray-800', icon: FiXCircle },
      error: { color: 'bg-red-100 text-red-800', icon: FiAlertCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FiAlertCircle }
    };
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!integration) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Integration not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/integrations"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <FiArrowLeft className="mr-2" />
          Back to Integrations
        </Link>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{integration.name}</h1>
            <p className="mt-1 text-sm text-gray-500">{integration.description}</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={handleToggle}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              {integration.is_enabled ? (
                <>
                  <FiToggleRight className="mr-2 text-green-500" />
                  Enabled
                </>
              ) : (
                <>
                  <FiToggleLeft className="mr-2 text-gray-400" />
                  Disabled
                </>
              )}
            </button>
            <button
              onClick={handleTest}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiCheckCircle className="mr-2" />
              Test
            </button>
            <button
              onClick={handleSync}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiRefreshCw className="mr-2" />
              Sync
            </button>
            <Link
              to={`/app/integrations/${id}/edit`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <FiEdit className="mr-2" />
              Edit
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              <FiTrash2 className="mr-2" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Integration Type</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">{integration.integration_type}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Provider</dt>
              <dd className="mt-1 text-sm text-gray-900">{integration.provider || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Status</dt>
              <dd className="mt-1">{getStatusBadge(integration.status)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Enabled</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {integration.is_enabled ? 'Yes' : 'No'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created By</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {integration.creator?.first_name} {integration.creator?.last_name}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created At</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {new Date(integration.created_at).toLocaleString()}
              </dd>
            </div>
          </dl>
        </div>

        {/* Connection Settings */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Connection Settings</h2>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Endpoint URL</dt>
              <dd className="mt-1 text-sm text-gray-900 break-all">
                {integration.endpoint_url || 'N/A'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Authentication Method</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">
                {integration.auth_method.replace('_', ' ')}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Sync Frequency</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">
                {integration.sync_frequency}
              </dd>
            </div>
          </dl>
        </div>

        {/* Sync Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Sync Information</h2>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Sync</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {integration.last_sync_at
                  ? new Date(integration.last_sync_at).toLocaleString()
                  : 'Never'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Last Sync Status</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">
                {integration.last_sync_status || 'N/A'}
              </dd>
            </div>
            {integration.error_message && (
              <div className="sm:col-span-2">
                <dt className="text-sm font-medium text-gray-500">Error Message</dt>
                <dd className="mt-1 text-sm text-red-600">{integration.error_message}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Configuration */}
        {integration.configuration && Object.keys(integration.configuration).length > 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Configuration</h2>
            <pre className="bg-gray-50 p-4 rounded-md overflow-x-auto text-sm">
              {JSON.stringify(integration.configuration, null, 2)}
            </pre>
          </div>
        )}

        {/* Metadata */}
        {integration.metadata && Object.keys(integration.metadata).length > 0 && (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Metadata</h2>
            <pre className="bg-gray-50 p-4 rounded-md overflow-x-auto text-sm">
              {JSON.stringify(integration.metadata, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegrationView;

